/**
 * Deployment-refresh detection against the SPA's own origin.
 *
 * The bundle and /version.json are emitted by the same build and deploy
 * together (SWA or App Service wwwroot), so comparing the baked version to
 * the served version.json can never disagree about *which* release is live —
 * cross-pipeline backend/frontend comparisons previously reload-looped the
 * SPA whenever the two pipelines skewed (SWA-first staging, 2026-07-07).
 */

/** Build metadata (`+N`) carries no release identity — `23.1.3+6` is `23.1.3`. */
export function stripBuildMetadata(version: string): string {
  return version.split('+')[0]
}

export function isNewDeployment(
  bakedVersion: string,
  deployedVersion: string | null | undefined,
): boolean {
  if (!bakedVersion || !deployedVersion) return false
  return stripBuildMetadata(bakedVersion) !== stripBuildMetadata(deployedVersion)
}

/** Resolves the currently deployed SPA version, or null when unknowable (fail open). */
export async function fetchDeployedVersion(
  fetchImpl: typeof fetch = fetch,
): Promise<string | null> {
  try {
    const response = await fetchImpl(`/version.json?t=${Date.now()}`, { cache: 'no-store' })
    if (!response.ok) return null
    const payload = await response.json()
    return typeof payload?.version === 'string' ? payload.version : null
  } catch {
    return null
  }
}

interface VersionGuardDeps {
  bakedVersion: string
  fetchDeployedVersion: () => Promise<string | null>
  reload: (path: string) => void
  storage: Pick<Storage, 'getItem' | 'setItem'>
  onNewVersion?: (version: string) => void
  now?: () => number
  throttleMs?: number
}

const RELOADED_FOR_VERSION_KEY = 'ironsight:reloaded-for-version'

/**
 * Router guard factory. Navigation is never blocked: next() is called
 * synchronously and the version check runs in the background. On a new
 * deployment the page hard-reloads to the target path — at most once per
 * deployed version (sessionStorage one-shot), so a check that somehow keeps
 * mismatching degrades to a badge, never a reload loop.
 */
export function createVersionGuard({
  bakedVersion,
  fetchDeployedVersion: fetchDeployed,
  reload,
  storage,
  onNewVersion,
  now = () => Date.now(),
  throttleMs = 60_000,
}: VersionGuardDeps) {
  let lastCheckedAt: number | null = null

  return (to: { fullPath: string }, _from: unknown, next: () => void): Promise<void> => {
    next()

    if (lastCheckedAt !== null && now() - lastCheckedAt < throttleMs) {
      return Promise.resolve()
    }
    lastCheckedAt = now()

    return fetchDeployed()
      .then((deployedVersion) => {
        if (!isNewDeployment(bakedVersion, deployedVersion)) return

        onNewVersion?.(deployedVersion as string)

        if (storage.getItem(RELOADED_FOR_VERSION_KEY) === deployedVersion) return
        storage.setItem(RELOADED_FOR_VERSION_KEY, deployedVersion as string)
        reload(to.fullPath)
      })
      .catch(() => undefined)
  }
}
